import React = require('react');
import {FunctionComponent, useMemo, useState} from 'react';
import {classNames} from '../../Support/classNames';
import {FeatureFlags} from '../../../FeatureFlags';
import {DualRange} from '../Form/DualRange';
import {HeartRateTrack} from '../../../Models/HeartRateTrack';
import {GeoTrack} from '../../../Models/GeoTrack';
import {Duration} from '../../../Domain/Duration';
import {OutdoorsSplitGraph} from './OutdoorsSplitGraph';
import {OutdoorsHeartRateGraph} from './OutdoorsHeartRateGraph';
import {GeoTrackStrokes} from '../../../Models/GeoTrackStrokes';
import {OutdoorsStrokeRateGraph} from './OutdoorsStrokeRateGraph';

interface OwnProps {
    geoTrack: GeoTrack;
    geoTrackStrokes?: GeoTrackStrokes | null;
    heartRateTrack: HeartRateTrack | null;
    className?: string;
}

export const OutdoorsGraphs: FunctionComponent<OwnProps> = ({geoTrack, geoTrackStrokes, heartRateTrack, className}) => {
    const fullElapsedTimeRange = {
        min: geoTrack[0].elapsed_time,
        max: geoTrack[geoTrack.length - 1].elapsed_time,
    };
    const [range, setRange] = useState<{min: Duration; max: Duration}>(fullElapsedTimeRange);
    const [activeElapsedTime, setActiveElapsedTime] = useState<{x: number; elapsedTime: Duration} | null>(null);

    const heartRateTrackSelection = useMemo(() => {
        return heartRateTrack?.filter((point) => {
            return point.elapsed_time.seconds >= range.min.seconds && point.elapsed_time.seconds <= range.max.seconds;
        });
    }, [heartRateTrack, range.min.seconds, range.max.seconds]);

    const geoTrackSelection = useMemo(() => {
        return geoTrack.filter((point) => {
            return point.elapsed_time.seconds >= range.min.seconds && point.elapsed_time.seconds <= range.max.seconds;
        });
    }, [geoTrack, range.min.seconds, range.max.seconds]);

    const geoTrackStrokesSelection = useMemo(() => {
        return (
            geoTrackStrokes?.filter((point) => {
                return point.elapsedTime.gte(range.min) && point.elapsedTime.lte(range.max);
            }) || []
        );
    }, [geoTrackStrokes, range.min.seconds, range.max.seconds]);

    return (
        <div className={classNames('card mt-3', className)} onMouseLeave={() => setActiveElapsedTime(null)}>
            <div className="card-title">
                <h5>Graphs</h5>
            </div>
            <div className="card-body graphs">
                <div className="row">
                    <div className="col-12">
                        <p className="graphs__label">Split (/500m)</p>
                        <OutdoorsSplitGraph
                            tooltipAt={activeElapsedTime}
                            onTooltipActivated={setActiveElapsedTime}
                            range={range}
                            geoTrack={geoTrackSelection}
                        />
                    </div>
                </div>
                {geoTrackStrokesSelection && (
                    <div className="row">
                        <div className="col-12">
                            <p className="graphs__label">Stroke rate</p>
                            <OutdoorsStrokeRateGraph
                                tooltipAt={activeElapsedTime}
                                onTooltipActivated={setActiveElapsedTime}
                                range={range}
                                geoTrackStrokes={geoTrackStrokesSelection}
                            />
                        </div>
                    </div>
                )}
                {heartRateTrackSelection && (
                    <div className="row">
                        <div className="col-12">
                            <p className="graphs__label">Heartrate (bpm)</p>
                            <OutdoorsHeartRateGraph
                                tooltipAt={activeElapsedTime}
                                onTooltipActivated={setActiveElapsedTime}
                                range={range}
                                heartRateTrack={heartRateTrackSelection}
                            />
                        </div>
                    </div>
                )}
                {FeatureFlags.graphRangeSelection && (
                    <div className="row">
                        <div className="col-12">
                            <DualRange
                                value={{min: Math.floor(range.min.seconds), max: Math.ceil(range.max.seconds)}}
                                onChange={(r) => {
                                    setActiveElapsedTime(null);
                                    setRange({
                                        min: Duration.fromSeconds(r.min),
                                        max: Duration.fromSeconds(r.max),
                                    });
                                }}
                                min={Math.floor(fullElapsedTimeRange.min.seconds)}
                                max={Math.ceil(fullElapsedTimeRange.max.seconds)}
                                step={1}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
